import axios from "axios";
import keycloak from "../Keycloak";

const serverHostURL = window.location.protocol + "//" + window.location.host;
const generalApiURL = `${serverHostURL}/api`;
const generalCardURL = `${serverHostURL}/card`;

export const API_URL = process.env.REACT_APP_FORCE_API_URL
  ? `${process.env.REACT_APP_FORCE_API_URL}/api`
  : generalApiURL;
export const CARD_URL = process.env.REACT_APP_FORCE_CARD_URL
  ? `${process.env.REACT_APP_FORCE_CARD_URL}/api`
  : generalCardURL;

export const IMAGE_HOST_LINK =
  "https://jura-storage.fra1.cdn.digitaloceanspaces.com/businessCardImages";

export interface CardInfo {
  idArea: number; //defines in what "area" a user is, can be a country or whatever
  surname: string;
  firstName: string;
  jobDescription: string;
  employeeNumber: string;
  email: string;
  telephone: string;
  telephone_1?: string;
  fax?: string;
  mobile?: string;
  website: string;
  idLayout: number;
  nfcId: string;
  pictureExtension?: string;
  address?: string;
  chosen_address_1_index?: number;

  nfcLink?: string; // doesn't exist on backend, but used here for the user entry rows visual
  layoutDisplayName?: string; // also added here on the frontend, for convenience
  areaDisplayName?: string; // ^
}

export interface OperatorInfo {
  email: string;
  role: OperatorRole;
  idArea: number;
  firstName: string;
  surname: string;
  activated: boolean;

  areaDisplayName?: string; // also for frontend convenience, like CardInfo
}

export interface CreateAreaInfo {
  name: string;
  disclaimerText: string;
  disclaimerTitle: string;
}
export interface AreaInfo extends CreateAreaInfo {
  idArea: number;
}

export interface CreateLayoutInfo {
  name: string;
  label: string;
  website: string;
  notice: string;
  address: string;
  address_1?: string;
  address_1_labels?: string;
  type: string;
  idArea: number;
  customCompanyName?: string;
  overrideDisclaimerTitle?: string;
  overrideDisclaimerText?: string;
}
export interface LayoutInfo extends CreateLayoutInfo {
  idLayout: number;
}

export enum LayoutType {
  EMPLOYEE_CARD = "employee-card",
  BASIC_CARD = "basic-card",
  DE_V3 = "de-v3",
}

export enum OperatorRole {
  ADMIN = "admin",
  MANAGER = "manager",
  UNSET = "unset", // for uses not yet regsitered on the businesscard db
}

// interfacing functions ----------------------------------------

export const getOperatorDetails = async (email: string): Promise<OperatorInfo | undefined> => {
  try {
    const operatorInfo = await axios.get(
      `${API_URL}/businesscard/getOperatorEntry?email=${email}`,
      { headers: { Authorization: `bearer ${keycloak.token}` } }
    );
    console.log("operator info:", operatorInfo.data);
    return operatorInfo.data;
  } catch (e) {
    console.error("Couldn't get the operator details for the current user");
  }
  return undefined;
};

export const getAvailableOperatorInfos = async (): Promise<OperatorInfo[] | undefined> => {
  try {
    const operatorInfos = await axios.get(`${API_URL}/businesscard/getAllOperatorEntries`, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return operatorInfos.data;
  } catch (e) {
    console.error("Couldn't get the operator info enetries");
  }
  return undefined;
};

export const getAvailableCardInfos = async (): Promise<CardInfo[] | undefined> => {
  try {
    const cardInfos = await axios.get(`${API_URL}/businesscard/getAllUserCardEntries`, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return cardInfos.data;
  } catch (e) {
    console.error("Couldn't get the card info entries");
  }
  return undefined;
};

export const getCardInfo = async (nfcId: string): Promise<CardInfo | undefined> => {
  try {
    const cardInfo = await axios.get(`${API_URL}/businesscard/getUserCardEntry?nfcId=${nfcId}`);
    return cardInfo.data;
  } catch (e) {
    console.error("Couldn't get the card info using id", nfcId, e);
  }
  return undefined;
};

export const deleteCardUser = async (nfcId: string): Promise<boolean> => {
  try {
    const res = await axios.delete(`${API_URL}/businesscard/deleteUserEntry`, {
      data: { nfcId: nfcId },
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return true;
  } catch (e) {
    console.error(e);
  }
  return false;
};

export const updateCardInfo = async (cardInfo: CardInfo): Promise<boolean> => {
  try {
    const res = await axios.put(`${API_URL}/businesscard/updateUserEntry`, cardInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error("Couldn't update the card", cardInfo);
  }
  return false;
};

export const createNewCard = async (cardInfo: CardInfo): Promise<CardInfo | undefined> => {
  try {
    const res = await axios.post(`${API_URL}/businesscard/addUserCard`, cardInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.data;
  } catch (e) {
    console.error("Couldn't create the card", cardInfo);
  }
  return undefined;
};

export const sendOperatorInvitation = async (
  email: string,
  role: string,
  idArea: number
): Promise<boolean> => {
  try {
    const success = await axios.post(
      `${API_URL}/administration/inviteNewOperator`,
      {
        idArea: idArea,
        email: email,
        role: role,
      },
      {
        headers: { Authorization: `bearer ${keycloak.token}` },
      }
    );
    if (success.status === 201) {
      return true;
    } else {
      return false;
    }
  } catch (e) {
    console.error("failed to invite the user", email, role, idArea);
  }
  return false;
};

export const deleteOperator = async (email: string): Promise<boolean> => {
  try {
    const res = await axios.delete(`${API_URL}/businesscard/deleteOperatorEntry`, {
      data: { email: email },
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return true;
  } catch (e) {}
  return false;
};

export const updateOperatorValues = async (operatorInfo: OperatorInfo): Promise<boolean> => {
  try {
    const res = await axios.put(`${API_URL}/businesscard/updateOperatorEntry`, operatorInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error("couldn't udpate operator values", operatorInfo);
  }
  return false;
};

export const addAreaEntry = async (areaInfo: CreateAreaInfo): Promise<boolean> => {
  try {
    const res = await axios.post(`${API_URL}/businesscard/addAreaEntry`, areaInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error("couldn't create area", areaInfo);
  }
  return false;
};

export const getAreaEntry = async (idArea: number): Promise<AreaInfo | undefined> => {
  try {
    const res = await axios.get(`${API_URL}/businesscard/getAreaEntry?idArea=${idArea}`, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }

  return undefined;
};

export const getAllAreaEntries = async (): Promise<AreaInfo[]> => {
  try {
    const res = await axios.get(`${API_URL}/businesscard/getAllAreaEntries`, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }
  return [];
};

export const deleteAreaEntry = async (idArea: number): Promise<boolean> => {
  try {
    const res = await axios.delete(`${API_URL}/businesscard/deleteAreaEntry`, {
      data: { idArea: idArea },
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 200;
  } catch (e) {
    console.error(e);
  }
  return false;
};

export const updateAreaEntry = async (areaInfo: AreaInfo): Promise<boolean> => {
  try {
    const res = await axios.put(`${API_URL}/businesscard/updateAreaEntry`, areaInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error(e);
  }

  return false;
};

export const addLayoutEntry = async (layoutInfo: CreateLayoutInfo): Promise<boolean> => {
  try {
    const res = await axios.post(`${API_URL}/businesscard/addLayoutEntry`, layoutInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error("couldn't create layout", layoutInfo);
  }
  return false;
};

export const getLayoutEntry = async (idLayout: number): Promise<LayoutInfo | undefined> => {
  try {
    const res = await axios.get(`${API_URL}/businesscard/getLayoutEntry?idLayout=${idLayout}`, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }

  return undefined;
};

export const getAllLayoutEntries = async (idArea?: number): Promise<LayoutInfo[]> => {
  try {
    const res = await axios.get(
      `${API_URL}/businesscard/getAllLayoutEntries${
        idArea !== undefined ? `?idArea=${idArea}` : ""
      }`,
      {
        headers: { Authorization: `bearer ${keycloak.token}` },
      }
    );
    return res.data;
  } catch (e) {
    console.error(e);
  }
  return [];
};

export const deleteLayyoutEntry = async (idLayout: number): Promise<boolean> => {
  try {
    const res = await axios.delete(`${API_URL}/businesscard/deleteLayoutEntry`, {
      data: { idLayout: idLayout },
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 200;
  } catch (e) {
    console.error(e);
  }
  return false;
};

export const updateLayoutEntry = async (layoutInfo: LayoutInfo): Promise<boolean> => {
  try {
    const res = await axios.put(`${API_URL}/businesscard/updateLayoutEntry`, layoutInfo, {
      headers: { Authorization: `bearer ${keycloak.token}` },
    });
    return res.status === 201;
  } catch (e) {
    console.error(e);
  }

  return false;
};

export const getCardPdf = async (
  nfcId: string,
  cardLabelTranslated: string,
  cardNoticeTranslated: string
): Promise<any> => {
  try {
    const res = await axios.get(`${API_URL}/businesscard/getUserCardPdf?nfcId=${nfcId}`, {
      responseType: "blob",
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }
  return undefined;
};

export const getVCard = async (nfcId: string): Promise<any> => {
  try {
    const res = await axios.get(`${API_URL}/cards/generateVCard?nfcId=${nfcId}`, {
      responseType: "blob",
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }
};

// other backends (e.g. uploading images)

/**
 * Uploads to the backend, which then uploads it to the actual image storage server, if the permissions are set
 * @param file
 * @returns
 */
export const uploadImage = async (file: File, forcedName: string): Promise<boolean> => {
  console.log("forcednamestring", forcedName);
  try {
    const formData = new FormData();
    formData.append("image", file, forcedName);

    const res = await axios.post(`${API_URL}/upload/uploadBusinesscardImage`, formData, {
      // const res = await axios.post(`http://localhost:3002/files/business-card-image`, formData, {
      headers: { Authorization: `bearer ${keycloak.token}`, "Content-Type": "multipart/form-data" },
    });

    return res.status === 201;
  } catch (e) {
    console.error(e);
  }

  return false;
};

export const deleteCardImage = async (nfcId: string): Promise<boolean> => {
  try {
    const res = await axios.delete(`${API_URL}/upload/deleteBusinesscardImage`, {
      data: { nfcId: nfcId },
      headers: { Authorization: `bearer ${keycloak.token}` },
    });

    return res.status === 200;
  } catch (e) {
    console.error(e);
  }

  return false;
};

export const getImage = async (
  nfcId: string,
  pictureExtension: string
): Promise<Blob | undefined> => {
  try {
    const res = await fetch(`${IMAGE_HOST_LINK}/${nfcId}${pictureExtension}`, {});

    if (!res || res.status !== 200) {
      throw new Error("couldn't find image");
    }
    return await res.blob();
  } catch (e) {
    console.error(e);
  }
  return undefined;
};
